import { useState, useEffect } from 'react';
import { Key, X, AlertCircle, Check, Loader2, CreditCard, Coins, Zap, Wallet, Package } from 'lucide-react';
import { api, rechargePackageAPI, paymentAPI, paymentConfigAPI } from '../../services/api';
import type { RechargePackage } from '../../services/api';

interface RedeemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (credits: number) => void;
}

type TabType = 'recharge' | 'packages' | 'code';

export function RedeemModal({ isOpen, onClose, onSuccess }: RedeemModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('packages');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // 充值相关状态
  const [packages, setPackages] = useState<RechargePackage[]>([]);
  const [paymentEnabled, setPaymentEnabled] = useState(false);
  const [enabledPayTypes, setEnabledPayTypes] = useState<string[]>(['alipay', 'wxpay', 'qqpay']);
  const [minRechargeAmount, setMinRechargeAmount] = useState(0.01);
  const [customAmount, setCustomAmount] = useState('');
  const [selectedPayType, setSelectedPayType] = useState('alipay');
  const [rechargeLoading, setRechargeLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      checkPaymentStatus();
      loadPackages();
    }
  }, [isOpen]);

  const checkPaymentStatus = async () => {
    try {
      const status = await paymentConfigAPI.getStatus();
      setPaymentEnabled(status.isEnabled);
      const payTypes = status.enabledPayTypes || ['alipay', 'wxpay', 'qqpay'];
      setEnabledPayTypes(payTypes);
      // 设置最低充值金额
      setMinRechargeAmount(status.minRechargeAmount ?? 0.01);
      // 默认选中第一个启用的支付方式
      if (payTypes.length > 0) {
        setSelectedPayType(payTypes[0]);
      }
    } catch {
      setPaymentEnabled(false);
      setEnabledPayTypes(['alipay', 'wxpay', 'qqpay']);
      setMinRechargeAmount(0.01);
    }
  };

  const loadPackages = async () => {
    try {
      const data = await rechargePackageAPI.getPackages();
      // 后端已经过滤了 isActive=true 的套餐，这里只需要排序
      setPackages(data.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)));
    } catch {
      setPackages([]);
    }
  };

  if (!isOpen) return null;

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await api.redeemCode(code.trim());
      setSuccess(`兑换成功！获得 ${res.redeemed} 积分`);
      onSuccess(res.credits);
      setCode('');
      setTimeout(() => onClose(), 1500);
    } catch (err: any) {
      setError(err.message || '兑换失败，请检查兑换码是否正确');
    } finally {
      setLoading(false);
    }
  };

  const handlePackageRecharge = async (pkg: RechargePackage) => {
    setRechargeLoading(true);
    setError('');
    setSuccess('');

    try {
      const order = await paymentAPI.createOrder({
        packageId: pkg.id,
        payType: selectedPayType,
      });

      // 跳转到支付页面前重置 loading，防止用户返回后按钮不可用
      setRechargeLoading(false);
      // 跳转到支付页面
      window.location.href = order.payUrl;
    } catch (err: any) {
      setError(err.message || '创建订单失败');
      setRechargeLoading(false);
    }
  };

  const handleCustomRecharge = async () => {
    const amount = parseFloat(customAmount);
    if (!amount || amount <= 0) {
      setError('请输入有效的充值金额');
      return;
    }

    if (amount < minRechargeAmount) {
      setError(`自定义充值金额不能低于 ${minRechargeAmount} 元`);
      return;
    }

    setRechargeLoading(true);
    setError('');
    setSuccess('');

    try {
      const order = await paymentAPI.createOrder({
        payType: selectedPayType,
        customAmount: amount,
      });

      // 跳转到支付页面前重置 loading，防止用户返回后按钮不可用
      setRechargeLoading(false);
      // 跳转到支付页面
      window.location.href = order.payUrl;
    } catch (err: any) {
      setError(err.message || '创建订单失败');
      setRechargeLoading(false);
    }
  };

  const getPayTypeIcon = (type: string) => {
    switch (type) {
      case 'alipay':
        return <div className="w-6 h-6 rounded bg-blue-500 flex items-center justify-center text-white text-xs font-bold">支</div>;
      case 'wxpay':
        return <div className="w-6 h-6 rounded bg-green-500 flex items-center justify-center text-white text-xs font-bold">微</div>;
      case 'qqpay':
        return <div className="w-6 h-6 rounded bg-indigo-500 flex items-center justify-center text-white text-xs font-bold">Q</div>;
      default:
        return <CreditCard className="w-5 h-5 text-gray-500" />;
    }
  };

  const getPayTypeName = (type: string) => {
    switch (type) {
      case 'alipay': return '支付宝';
      case 'wxpay': return '微信支付';
      case 'qqpay': return 'QQ钱包';
      default: return '支付';
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Coins className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">兑换积分</h3>
              <p className="text-xs text-gray-500">获取积分以生成更多图片</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs - 套餐购买、在线充值、兑换码 */}
        <div className="flex border-b border-gray-100">
          {paymentEnabled && (
            <>
              <button
                onClick={() => setActiveTab('packages')}
                className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'packages'
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Package className="w-4 h-4" />
                套餐购买
              </button>
              <button
                onClick={() => setActiveTab('recharge')}
                className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'recharge'
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <CreditCard className="w-4 h-4" />
                在线充值
              </button>
            </>
          )}
          <button
            onClick={() => setActiveTab('code')}
            className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-medium transition-colors ${
              activeTab === 'code'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Key className="w-4 h-4" />
            兑换码
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'code' && (
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">兑换码</label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="请输入兑换码"
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 shadow-sm transition-all uppercase"
                />
                <p className="text-xs text-gray-500 mt-2">输入兑换码即可获取对应积分</p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <p className="text-sm text-green-600">{success}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !code.trim()}
                className="w-full bg-blue-600 text-white font-medium py-3 rounded-xl hover:bg-blue-700 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    兑换中...
                  </span>
                ) : (
                  '立即兑换'
                )}
              </button>
            </form>
          )}

          {activeTab === 'packages' && paymentEnabled && (
            <div className="space-y-6">
              {/* 支付方式选择 */}
              {enabledPayTypes.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-3 block">选择支付方式</label>
                  <div className={`grid gap-3 ${enabledPayTypes.length === 1 ? 'grid-cols-1' : enabledPayTypes.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                    {enabledPayTypes.map((type) => (
                      <button
                        key={type}
                        onClick={() => setSelectedPayType(type)}
                        className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition-all ${
                          selectedPayType === type
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                      >
                        {getPayTypeIcon(type)}
                        <span className="text-sm font-medium">{getPayTypeName(type)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* 充值套餐 */}
              {packages.length > 0 ? (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-3 block">选择充值套餐</label>
                  <div className="grid grid-cols-3 gap-2">
                    {packages.map((pkg) => (
                      <button
                        key={pkg.id}
                        onClick={() => handlePackageRecharge(pkg)}
                        disabled={rechargeLoading}
                        className="relative p-2.5 rounded-lg border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50/30 transition-all text-left group"
                      >
                        {pkg.sortOrder === 0 && (
                          <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] px-1.5 py-0 rounded-full">
                            推荐
                          </span>
                        )}
                        <div className="flex items-center gap-1 mb-1">
                          <Zap className="w-3 h-3 text-yellow-500" />
                          <span className="font-semibold text-gray-900 text-xs">{pkg.name}</span>
                        </div>
                        <div className="flex items-baseline gap-0.5 mb-0.5">
                          <span className="text-[10px] text-gray-500">¥</span>
                          <span className="text-lg font-bold text-blue-600">{pkg.amount}</span>
                        </div>
                        <div className="flex items-center gap-0.5 text-xs text-gray-600">
                          <Coins className="w-3 h-3" />
                          <span>{pkg.credits} 积分</span>
                        </div>
                        {pkg.description && (
                          <p className="text-[10px] text-gray-500 mt-1 leading-tight">{pkg.description}</p>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">暂无充值套餐</p>
                  <p className="text-sm text-gray-400 mt-1">请使用自定义金额充值</p>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'recharge' && paymentEnabled && (
            <div className="space-y-6">
              {/* 支付方式选择 */}
              {enabledPayTypes.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-3 block">选择支付方式</label>
                  <div className={`grid gap-3 ${enabledPayTypes.length === 1 ? 'grid-cols-1' : enabledPayTypes.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                    {enabledPayTypes.map((type) => (
                      <button
                        key={type}
                        onClick={() => setSelectedPayType(type)}
                        className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition-all ${
                          selectedPayType === type
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                      >
                        {getPayTypeIcon(type)}
                        <span className="text-sm font-medium">{getPayTypeName(type)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* 自定义金额 */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-3 block">
                  自定义充值金额
                  <span className="text-xs text-gray-400 font-normal ml-2">（最低 {minRechargeAmount} 元）</span>
                </label>
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">¥</span>
                    <input
                      type="number"
                      min={minRechargeAmount}
                      step="1"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      placeholder={`最低 ${minRechargeAmount} 元`}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-8 pr-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 shadow-sm transition-all"
                    />
                  </div>
                  <button
                    onClick={handleCustomRecharge}
                    disabled={rechargeLoading || !customAmount || parseFloat(customAmount) < minRechargeAmount}
                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-xl hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {rechargeLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      '充值'
                    )}
                  </button>
                </div>
                
                {/* 快捷金额 */}
                <div className="flex flex-wrap gap-2 mt-3">
                  {[1, 5, 10, 20, 50, 100].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setCustomAmount(amount.toString())}
                      className={`px-2 py-1 text-xs rounded border transition-all ${
                        customAmount === amount.toString()
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/30 text-gray-600'
                      }`}
                    >
                      {amount}元
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
            </div>
          )}

          {!paymentEnabled && (activeTab === 'recharge' || activeTab === 'packages') && (
            <div className="text-center py-12">
              <Wallet className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">在线充值功能暂未开启</p>
              <p className="text-sm text-gray-400 mt-1">请使用兑换码获取积分</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
